import { useEffect, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  AlertTriangle,
  BookOpen,
  Camera,
  ChevronDown,
  FileText,
  Image as ImageIcon,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { useStorageUrl } from '../../lib/storageUrl';
import { DateField } from '../../components/DateField';
import { ModalClose } from '../../components/ModalClose';
import { DEMO_MAINTENANCE } from '../../lib/demoData';
import { annualDueFrom } from '../../lib/maintenance';
import { localToday } from '../../lib/flightFilters';
import {
  DEMO_READ,
  MAX_READ_PAGES,
  entryNotes,
  matchTrackerItem,
  readFailureMessage,
  readLogbookPages,
  shrinkPhoto,
  trackerPatch,
  type ReadEntry,
} from '../../lib/logbookRead';
import type {
  AircraftDoc,
  AircraftLogbookEntryDoc as LogbookEntryDoc,
  LogbookBook,
  LogbookPage,
  MaintenanceItemDoc,
} from '../../lib/types';

// ─────────────────────────────────────────────────────────────────────────────
// The logbooks, photographed.
//
// The paper books are still the legal record and still live in the airplane or
// the owner's safe. What they are NOT is reachable: an insurer asks for the
// last annual, a buyer asks for the engine history, a mechanic asks what the
// tach read at the last oil change — and somebody drives to the hangar.
//
// So: an owner stands at the open book with a phone, taps "Take photo", and
// the page lands against the airplane with the date, the book it came from and
// the reading written on it. Pages are Storage paths, resolved at render time,
// so a leaked Firestore doc still leaks no images.
// ─────────────────────────────────────────────────────────────────────────────

const BOOKS: { key: LogbookBook; label: string }[] = [
  { key: 'airframe', label: 'Airframe' },
  { key: 'engine', label: 'Engine' },
  { key: 'propeller', label: 'Propeller' },
  { key: 'avionics', label: 'Avionics' },
  { key: 'other', label: 'Other' },
];
const BOOK_LABEL: Record<LogbookBook, string> = {
  airframe: 'Airframe',
  engine: 'Engine',
  propeller: 'Propeller',
  avionics: 'Avionics',
  other: 'Other',
};

const MAX_BYTES = 15 * 1024 * 1024;
// Local, not UTC (see flightFilters.localToday).
const todayStr = () => localToday();

function fmtDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function AircraftLogbookSection({
  aircraft,
  demoMode,
  onAircraftPatch,
}: {
  aircraft?: AircraftDoc;
  demoMode?: boolean;
  /** Demo only: the page's local aircraft list (live reads the snapshot). */
  onAircraftPatch?: (patch: Partial<AircraftDoc>) => void;
}) {
  const { fbUser } = useAuth();
  const [entries, setEntries] = useState<LogbookEntryDoc[]>([]);
  const [editing, setEditing] = useState<LogbookEntryDoc | 'new' | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!aircraft) return;
    if (demoMode) {
      // Nothing seeded: a demo school's books are whatever the visitor shoots,
      // and the sample personas would otherwise show somebody else's paperwork.
      setEntries([]);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'aircraftLogbook'), where('aircraftId', '==', aircraft.aircraftId)),
      (snap) => setEntries(snap.docs.map((d) => ({ ...(d.data() as LogbookEntryDoc), entryId: d.id })))
    );
    return () => unsub();
    // Keyed on the id: a patch to the aircraft (e.g. its annual date) must not
    // reload the list and throw away what was just marked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft?.aircraftId, demoMode]);

  // The tracker items a read entry can mark done ("Annual inspection" → the
  // Annual item). Read here so the review screen can offer it per entry.
  const [mxItems, setMxItems] = useState<MaintenanceItemDoc[]>([]);
  useEffect(() => {
    if (!aircraft) return;
    if (demoMode) {
      setMxItems(DEMO_MAINTENANCE.filter((m) => m.aircraftId === aircraft.aircraftId));
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'maintenance'), where('aircraftId', '==', aircraft.aircraftId)),
      (snap) => setMxItems(snap.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))
    );
    return () => unsub();
    // Keyed on the id: a patch to the aircraft (e.g. its annual date) must not
    // reload the list and throw away what was just marked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft?.aircraftId, demoMode]);

  if (!aircraft) return null;
  const acId = aircraft.aircraftId;
  const sorted = [...entries].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  // Entries read from one set of photos share those pages — count each once.
  const pageCount = new Set(entries.flatMap((e) => (e.pages ?? []).map((p) => p.path))).size;

  async function save(data: Partial<LogbookEntryDoc>, existing?: LogbookEntryDoc) {
    if (demoMode || !db) {
      if (existing) {
        setEntries((cur) => cur.map((e) => (e.entryId === existing.entryId ? { ...e, ...data } : e)));
      } else {
        setEntries((cur) => [
          ...cur,
          { ...(data as LogbookEntryDoc), aircraftId: acId, entryId: `demo-${Date.now()}` },
        ]);
      }
      setEditing(null);
      return;
    }
    // Firestore here rejects `undefined` field values, and the form sends
    // undefined for a blank tach/Hobbs/notes: clear those on an edit, drop
    // them on a create.
    if (existing) {
      await updateDoc(doc(db, 'aircraftLogbook', existing.entryId), {
        ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v === undefined ? deleteField() : v])),
        updatedAt: serverTimestamp(),
      });
    } else {
      await addDoc(collection(db, 'aircraftLogbook'), {
        ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)),
        aircraftId: acId,
        createdAt: serverTimestamp(),
        createdByUid: fbUser?.uid ?? null,
      });
    }
    setEditing(null);
  }

  /** Save the entries the reader found, and any tracker items the owner ticked. */
  // A reading that marks the Annual done also moves the aircraft's annual
  // date — that's the one bookings and preflight check (see annualDueFrom).
  async function syncAnnual(rows: ReviewedEntry[]) {
    let due: string | null = null;
    for (const r of rows) {
      if (!r.tracker) continue;
      const item = mxItems.find((m) => m.itemId === r.tracker!.itemId);
      const d = item ? annualDueFrom(item, r.tracker.patch.dueDate) : null;
      if (d && (!due || d > due)) due = d;
    }
    if (!due || due === aircraft?.annualDueDate) return;
    onAircraftPatch?.({ annualDueDate: due });
    if (demoMode || !db) return;
    await updateDoc(doc(db, 'aircraft', acId), { annualDueDate: due });
  }

  async function saveMany(rows: ReviewedEntry[]) {
    if (demoMode || !db) {
      const stamp = Date.now();
      setEntries((cur) => [
        ...cur,
        ...rows.map((r, i) => ({ ...(r.data as LogbookEntryDoc), aircraftId: acId, entryId: `demo-${stamp}-${i}` })),
      ]);
      setMxItems((cur) =>
        cur.map((m) => {
          const hit = rows.find((r) => r.tracker?.itemId === m.itemId);
          return hit?.tracker ? { ...m, ...hit.tracker.patch } : m;
        })
      );
      await syncAnnual(rows);
      setEditing(null);
      return;
    }
    for (const r of rows) {
      await addDoc(collection(db, 'aircraftLogbook'), {
        ...Object.fromEntries(Object.entries(r.data).filter(([, v]) => v !== undefined)),
        aircraftId: acId,
        readByAi: true,
        createdAt: serverTimestamp(),
        createdByUid: fbUser?.uid ?? null,
      });
      if (r.tracker) {
        await updateDoc(doc(db, 'maintenance', r.tracker.itemId), {
          ...r.tracker.patch,
          updatedAt: serverTimestamp(),
        });
      }
    }
    await syncAnnual(rows);
    setEditing(null);
  }

  async function remove(entry: LogbookEntryDoc) {
    if (demoMode || !db) {
      setEntries((cur) => cur.filter((e) => e.entryId !== entry.entryId));
      return;
    }
    await deleteDoc(doc(db, 'aircraftLogbook', entry.entryId));
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <BookOpen size={18} className="shrink-0 text-navy-700" />
          <h2 className="font-display text-lg font-semibold text-navy-900">Logbooks</h2>
          <span className="truncate text-sm text-ink-muted">
            {entries.length === 0
              ? 'no pages yet'
              : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} · ${pageCount} ${
                  pageCount === 1 ? 'page' : 'pages'
                }`}
          </span>
          <ChevronDown
            size={18}
            className={`ml-auto shrink-0 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        <button
          className="btn-secondary inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm"
          onClick={() => {
            setOpen(true);
            setEditing('new');
          }}
        >
          <Camera size={15} /> Add pages
        </button>
      </div>

      {open && (
        <>
          {sorted.length === 0 ? (
            <div className="px-5 py-8 text-center sm:px-6">
              <p className="text-sm text-ink-muted">
                Photograph the airframe, engine and prop books once, and the history travels with the
                airplane — reachable from a phone at the hangar, and sendable to an insurer or a buyer
                without a drive to the field.
              </p>
              <button
                className="btn-primary mt-4 inline-flex items-center gap-1.5"
                onClick={() => setEditing('new')}
              >
                <Camera size={16} /> Take a photo or upload
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {sorted.map((e) => (
                <li key={e.entryId} className="px-5 py-4 sm:px-6">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-navy-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-navy-700">
                          {BOOK_LABEL[e.book] ?? 'Other'}
                        </span>
                        <span className="text-sm font-medium text-navy-900">{e.title}</span>
                        <span className="text-xs text-ink-muted">{fmtDate(e.date)}</span>
                      </div>
                      <div className="mt-0.5 text-xs text-ink-muted">
                        {typeof e.tach === 'number' ? `Tach ${e.tach.toFixed(1)}` : ''}
                        {typeof e.tach === 'number' && typeof e.hobbs === 'number' ? ' · ' : ''}
                        {typeof e.hobbs === 'number' ? `Hobbs ${e.hobbs.toFixed(1)}` : ''}
                      </div>
                      {e.notes && (
                        <p className="mt-1 whitespace-pre-line text-xs leading-snug text-ink-subtle">{e.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className="rounded p-1.5 text-navy-700 hover:bg-sky-50"
                        title="Edit"
                        onClick={() => setEditing(e)}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="rounded p-1.5 text-red-600 hover:bg-red-50"
                        title="Delete"
                        onClick={() => remove(e)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  {e.pages?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.pages.map((pg, i) => (
                        <PageThumb key={`${pg.path}-${i}`} page={pg} />
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {editing && (
        <LogbookForm
          entry={editing === 'new' ? undefined : editing}
          aircraft={aircraft}
          demoMode={demoMode}
          mxItems={mxItems}
          onClose={() => setEditing(null)}
          onSave={save}
          onSaveMany={saveMany}
        />
      )}
    </div>
  );
}

/** One photographed page: a real thumbnail for images, a document chip for PDFs. */
function PageThumb({ page }: { page: LogbookPage }) {
  const url = useStorageUrl(page.path);
  const isPdf = page.contentType === 'application/pdf' || /\.pdf$/i.test(page.name);

  if (isPdf) {
    return (
      <a
        href={url ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-1 text-center text-[10px] text-ink-muted hover:border-navy-600 ${
          url ? '' : 'pointer-events-none opacity-50'
        }`}
        title={page.name}
      >
        <FileText size={20} className="text-navy-700" />
        <span className="w-full truncate">{page.name}</span>
      </a>
    );
  }
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noopener noreferrer"
      className={`block h-20 w-20 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 ${
        url ? 'hover:border-navy-600' : 'pointer-events-none'
      }`}
      title={page.name}
    >
      {url ? (
        <img src={url} alt={page.name} className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-slate-400">
          <ImageIcon size={18} />
        </span>
      )}
    </a>
  );
}

/** One entry the reader found, as the owner has reviewed it. */
interface ReviewedEntry {
  data: Partial<LogbookEntryDoc>;
  tracker?: { itemId: string; patch: Partial<MaintenanceItemDoc> };
}

/** A reader entry on the review screen: editable fields + the owner's choices. */
interface Draft {
  read: ReadEntry;
  include: boolean;
  book: LogbookBook;
  date: string;
  title: string;
  tach: string;
  hobbs: string;
  notes: string;
  trackerItemId: string | null;
  updateTracker: boolean;
}

function LogbookForm({
  entry,
  aircraft,
  demoMode,
  mxItems,
  onClose,
  onSave,
  onSaveMany,
}: {
  entry?: LogbookEntryDoc;
  aircraft: AircraftDoc;
  demoMode?: boolean;
  mxItems: MaintenanceItemDoc[];
  onClose: () => void;
  onSave: (data: Partial<LogbookEntryDoc>, existing?: LogbookEntryDoc) => void;
  onSaveMany: (rows: ReviewedEntry[]) => Promise<void>;
}) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [book, setBook] = useState<LogbookBook>(entry?.book ?? 'airframe');
  const [date, setDate] = useState(entry?.date ?? todayStr());
  const [title, setTitle] = useState(entry?.title ?? '');
  const [tach, setTach] = useState(entry?.tach != null ? String(entry.tach) : '');
  const [hobbs, setHobbs] = useState(entry?.hobbs != null ? String(entry.hobbs) : '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [pages, setPages] = useState<LogbookPage[]>(entry?.pages ?? []);
  const [uploading, setUploading] = useState(0);
  const [err, setErr] = useState('');
  const [reading, setReading] = useState(false);
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [saving, setSaving] = useState(false);

  async function readPages() {
    setErr('');
    setReading(true);
    try {
      const res = demoMode
        ? await new Promise<typeof DEMO_READ>((r) => setTimeout(() => r(DEMO_READ), 1800))
        : await readLogbookPages({ aircraftId: aircraft.aircraftId, paths: pages.map((p) => p.path) });
      if (!res.ok) return setErr(readFailureMessage(res.reason));
      if (!res.usable || res.entries.length === 0) {
        return setErr(res.retake_hint || 'No maintenance entries found on these pages. Try a straighter, brighter photo.');
      }
      setDrafts(
        res.entries.map((e) => {
          const item = matchTrackerItem(e, mxItems);
          return {
            read: e,
            include: true,
            book: e.book,
            date: e.date || todayStr(),
            title: e.title,
            tach: e.tach != null ? String(e.tach) : '',
            hobbs: e.hobbs != null ? String(e.hobbs) : '',
            notes: entryNotes(e),
            trackerItemId: item?.itemId ?? null,
            // Off until the owner ticks it: a misread date must never move an
            // airworthiness due date on its own.
            updateTracker: false,
          };
        })
      );
      if (res.skipped.length) setErr(`Skipped (too large or not a photo/PDF): ${res.skipped.join(', ')}`);
    } catch {
      setErr('The reader didn’t answer. Check your connection and try again.');
    } finally {
      setReading(false);
    }
  }

  const patchDraft = (i: number, p: Partial<Draft>) =>
    setDrafts((cur) => (cur ? cur.map((d, j) => (j === i ? { ...d, ...p } : d)) : cur));

  async function saveDrafts() {
    if (!drafts) return;
    const chosen = drafts.filter((d) => d.include);
    if (chosen.length === 0) return setErr('Tick at least one entry to save.');
    if (chosen.some((d) => !d.title.trim() || !d.date)) return setErr('Every entry needs a name and a date.');
    setSaving(true);
    try {
      await onSaveMany(
        chosen.map((d) => {
          const tach = d.tach !== '' ? Number(d.tach) : undefined;
          const item = d.updateTracker && d.trackerItemId ? mxItems.find((m) => m.itemId === d.trackerItemId) : undefined;
          return {
            data: {
              book: d.book,
              date: d.date,
              title: d.title.trim(),
              tach,
              hobbs: d.hobbs !== '' ? Number(d.hobbs) : undefined,
              notes: d.notes.trim() || undefined,
              pages,
            },
            tracker: item
              ? { itemId: item.itemId, patch: trackerPatch({ ...d.read, date: d.date, tach: tach ?? null }, item) }
              : undefined,
          };
        })
      );
    } catch {
      setErr('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setErr('');
    const picked = Array.from(list);
    setUploading((n) => n + picked.length);
    for (const original of picked) {
      const file = await shrinkPhoto(original);
      if (file.size > MAX_BYTES) {
        setErr(`${file.name} is over 15 MB — photograph it again at a smaller size.`);
        setUploading((n) => n - 1);
        continue;
      }
      try {
        if (demoMode || !storage) {
          // Demo: a local preview only. Nothing leaves the browser.
          setPages((cur) => [
            ...cur,
            {
              path: URL.createObjectURL(file),
              name: file.name,
              contentType: file.type,
              size: file.size,
            },
          ]);
        } else {
          const safe = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
          const path = `maintenance/${aircraft.aircraftId}/logbook/${Date.now()}-${safe}`;
          await uploadBytes(storageRef(storage, path), file);
          setPages((cur) => [
            ...cur,
            { path, name: file.name, contentType: file.type, size: file.size },
          ]);
        }
      } catch {
        setErr(`Could not upload ${file.name}. Try again.`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (cameraInput.current) cameraInput.current.value = '';
    if (fileInput.current) fileInput.current.value = '';
  }

  function submit() {
    if (!title.trim()) return setErr('Give the entry a name — “Annual inspection”, “Oil change”.');
    if (pages.length === 0) return setErr('Add at least one photo or document.');
    onSave(
      {
        book,
        date,
        title: title.trim(),
        tach: tach !== '' ? Number(tach) : undefined,
        hobbs: hobbs !== '' ? Number(hobbs) : undefined,
        notes: notes.trim() || undefined,
        pages,
      },
      entry
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4 backdrop-blur-sm">
      <div className="card relative max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <ModalClose onClose={onClose} />
        <h3 className="pr-10 font-display text-lg font-semibold text-navy-900">
          {entry ? 'Edit' : 'Add'} logbook pages
        </h3>
        <p className="mt-1 text-sm text-ink-muted">
          {aircraft.tailNumber} — photograph the open book, or upload a scan the shop sent.
        </p>

        {/* Capture first: on a phone this is the whole feature. */}
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="sr-only"
          onChange={(e) => addFiles(e.target.files)}
        />
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="sr-only"
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => cameraInput.current?.click()}
            className="btn-primary inline-flex min-h-[44px] items-center justify-center gap-2"
          >
            <Camera size={17} /> Take photo
          </button>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            className="btn-secondary inline-flex min-h-[44px] items-center justify-center gap-2"
          >
            <Upload size={17} /> Choose files
          </button>
        </div>

        {pages.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {pages.map((pg, i) => (
              <div key={`${pg.path}-${i}`} className="relative">
                <PageThumb page={pg} />
                <button
                  type="button"
                  aria-label={`Remove ${pg.name}`}
                  onClick={() => setPages((cur) => cur.filter((_, j) => j !== i))}
                  className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-ink-muted shadow ring-1 ring-slate-200 hover:text-red-600"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        {uploading > 0 && (
          <p className="mt-2 text-xs text-ink-muted">
            Uploading {uploading} {uploading === 1 ? 'page' : 'pages'}…
          </p>
        )}

        {/* Read it for me: Claude fills the entries in from the photos. */}
        {!entry && !drafts && pages.length > 0 && (
          <div className="mt-4 rounded-xl border border-navy-900/10 bg-sky-50/60 p-3">
            <button
              type="button"
              onClick={readPages}
              disabled={reading || uploading > 0 || pages.length > MAX_READ_PAGES}
              className="btn-primary inline-flex min-h-[44px] w-full items-center justify-center gap-2"
            >
              <Sparkles size={17} />
              {reading ? 'Reading the pages… about a minute' : 'Read it for me'}
            </button>
            <p className="mt-2 text-xs text-ink-muted">
              {pages.length > MAX_READ_PAGES
                ? `Read up to ${MAX_READ_PAGES} pages at a time — remove a few and read the rest as a second entry.`
                : 'Fills in the date, tach, work done and ADs from the photos. You check every entry before it’s saved.'}
            </p>
          </div>
        )}

        {drafts ? (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-ink-muted">
              Found {drafts.length} {drafts.length === 1 ? 'entry' : 'entries'}. Check each one against the page —
              the paper book is still the record.
            </p>
            {drafts.map((d, i) => {
              const item = d.trackerItemId ? mxItems.find((m) => m.itemId === d.trackerItemId) : undefined;
              const flagged = d.read.confidence !== 'high' || !!d.read.check_note;
              return (
                <div
                  key={i}
                  className={`rounded-xl border p-3 ${
                    !d.include ? 'border-slate-200 opacity-60' : flagged ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'
                  }`}
                >
                  <label className="flex items-center gap-2 text-sm font-medium text-navy-900">
                    <input type="checkbox" checked={d.include} onChange={(e) => patchDraft(i, { include: e.target.checked })} />
                    Save this entry
                  </label>
                  {d.read.check_note && (
                    <p className="mt-2 flex gap-1.5 text-xs leading-snug text-amber-800">
                      <AlertTriangle size={14} className="mt-px shrink-0" /> {d.read.check_note}
                    </p>
                  )}
                  {d.include && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="label">Book</label>
                        <select className="input" value={d.book} onChange={(e) => patchDraft(i, { book: e.target.value as LogbookBook })}>
                          {BOOKS.map((b) => (
                            <option key={b.key} value={b.key}>
                              {b.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <label className="label">Date</label>
                        <DateField value={d.date} onChange={(e) => patchDraft(i, { date: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="label">What the entry covers</label>
                        <input className="input" value={d.title} onChange={(e) => patchDraft(i, { title: e.target.value })} />
                      </div>
                      <div>
                        <label className="label">Tach</label>
                        <input type="number" step="0.1" className="input" value={d.tach} onChange={(e) => patchDraft(i, { tach: e.target.value })} />
                      </div>
                      <div>
                        <label className="label">Hobbs</label>
                        <input type="number" step="0.1" className="input" value={d.hobbs} onChange={(e) => patchDraft(i, { hobbs: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="label">Notes</label>
                        <textarea className="input" rows={3} value={d.notes} onChange={(e) => patchDraft(i, { notes: e.target.value })} />
                      </div>
                      {item && (
                        <label className="col-span-2 flex items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm ring-1 ring-slate-200">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={d.updateTracker}
                            onChange={(e) => patchDraft(i, { updateTracker: e.target.checked })}
                          />
                          <span>
                            Also mark <strong>{item.label}</strong> done on {fmtDate(d.date)}
                            {d.tach !== '' ? ` at tach ${Number(d.tach).toFixed(1)}` : ''}
                            <span className="block text-xs text-ink-muted">
                              Rolls the next due date{item.intervalHours && d.tach !== '' ? ' and tach' : ''} from this entry.
                            </span>
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Which book</label>
            <select className="input" value={book} onChange={(e) => setBook(e.target.value as LogbookBook)}>
              {BOOKS.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Date of the entry</label>
            <DateField value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">What the entry covers</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Annual inspection"
            />
          </div>
          <div>
            <label className="label">Tach at the entry</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={tach}
              onChange={(e) => setTach(e.target.value)}
              placeholder={aircraft.currentTach.toFixed(1)}
            />
          </div>
          <div>
            <label className="label">Hobbs at the entry</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={hobbs}
              onChange={(e) => setHobbs(e.target.value)}
              placeholder={aircraft.currentHobbs.toFixed(1)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Notes (optional)</label>
            <textarea
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Shop, work order number, anything you'll want to find later."
            />
          </div>
        </div>
        )}

        {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
        {demoMode && (
          <p className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-ink-muted">
            {drafts
              ? 'Demo — this is a sample reading, not your photo. In your account it reads your own pages.'
              : 'Demo — your photo stays in this browser and is never uploaded.'}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {drafts ? (
            <button onClick={saveDrafts} disabled={saving} className="btn-primary">
              <Plus size={15} className="mr-1 inline" />
              {saving
                ? 'Saving…'
                : `Save ${drafts.filter((d) => d.include).length} ${drafts.filter((d) => d.include).length === 1 ? 'entry' : 'entries'}`}
            </button>
          ) : (
            <button onClick={submit} disabled={uploading > 0 || reading} className="btn-primary">
              <Plus size={15} className="mr-1 inline" /> Save entry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
